import { useState, useEffect, useCallback, memo } from 'react';

/**
 * Confetti Component
 *
 * Displays a burst of confetti particles for celebrations.
 * Originates from the right sidebar area where the download button is.
 */
const Confetti = memo(({ active, duration = 2000, particleCount = 50 }) => {
  const [particles, setParticles] = useState([]);

  const createParticles = useCallback(() => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
    const shapes = ['square', 'circle'];

    // Position confetti to originate from right sidebar area (download button location)
    // ~85% from left (right sidebar), ~15% from top (export section)
    return Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: 82 + (Math.random() - 0.5) * 10,
      y: 12 + (Math.random() - 0.5) * 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      size: 6 + Math.random() * 6,
      velocityX: (Math.random() - 0.5) * 20,
      velocityY: -6 - Math.random() * 6,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 20,
      delay: Math.random() * 100
    }));
  }, [particleCount]);

  useEffect(() => {
    if (active) {
      setParticles(createParticles());
      const timer = setTimeout(() => setParticles([]), duration);
      return () => clearTimeout(timer);
    }
  }, [active, createParticles, duration]);

  if (particles.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden'
      }}
    >
      {particles.map((particle) => (
        <div
          key={particle.id}
          style={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            borderRadius: particle.shape === 'circle' ? '50%' : '2px',
            transform: `rotate(${particle.rotation}deg)`,
            animation: `confetti-fall ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
            animationDelay: `${particle.delay}ms`,
            '--vx': particle.velocityX,
            '--vy': particle.velocityY,
            '--rot': particle.rotationSpeed
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translate(0, 0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translate(calc(var(--vx) * 30px), calc(var(--vy) * -50px + 400px)) rotate(calc(var(--rot) * 20deg));
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
});

export default Confetti;
